import React, { useState, useMemo } from 'react';
import './DetectionList.css';

function DetectionList({ detections = [], selectedIndex = null, onDetectionClick }) {
  const [groupByShelf, setGroupByShelf] = useState(true);
  
  // Group detections by shelf (class 0) based on Y coordinate
  const grouped = useMemo(() => {
    if (!groupByShelf || detections.length === 0) {
      return { ungrouped: detections };
    }
    
    // Find all shelves (class 0)
    const shelves = detections
      .map((det, index) => ({ ...det, originalIndex: index }))
      .filter(det => det.class === 0)
      .sort((a, b) => (a.cy || a.y || 0) - (b.cy || b.y || 0)); // Sort by Y coordinate
    
    // Find all products (not class 0)
    const products = detections
      .map((det, index) => ({ ...det, originalIndex: index }))
      .filter(det => det.class !== 0);
    
    if (shelves.length === 0) {
      // No shelves detected, group by Y coordinate ranges
      return groupByYCoordinate(products);
    }
    
    // Assign each product to nearest shelf
    const groups = {};
    shelves.forEach((shelf, idx) => {
      groups[`shelf_${idx}`] = {
        shelf,
        products: [],
        shelfNum: idx + 1
      };
    });
    
    products.forEach(product => {
      // Find nearest shelf by Y coordinate
      let nearestShelf = 0;
      const productY = product.cy || product.y || 0;
      const shelf0Y = shelves[0].cy || shelves[0].y || 0;
      let minDistance = Math.abs(productY - shelf0Y);
      
      shelves.forEach((shelf, idx) => {
        const shelfY = shelf.cy || shelf.y || 0;
        const distance = Math.abs(productY - shelfY);
        if (distance < minDistance) {
          minDistance = distance;
          nearestShelf = idx;
        }
      });
      
      groups[`shelf_${nearestShelf}`].products.push(product);
    });
    
    return groups;
  }, [detections, groupByShelf]);
  
  function groupByYCoordinate(products) {
    // Group products by Y coordinate ranges (approximate shelves)
    const sorted = [...products].sort((a, b) => {
      const aY = a.cy || a.y || 0;
      const bY = b.cy || b.y || 0;
      return aY - bY;
    });
    const groups = {};
    let currentGroup = 0;
    let lastY = -1;
    const threshold = 200; // 200 pixels difference in Y = new shelf
    
    sorted.forEach(product => {
      const productY = product.cy || product.y || 0;
      if (lastY === -1 || Math.abs(productY - lastY) > threshold) {
        currentGroup++;
        lastY = productY;
      }
      
      const groupKey = `shelf_${currentGroup - 1}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          shelf: null,
          products: [],
          shelfNum: currentGroup
        };
      }
      groups[groupKey].products.push(product);
    });
    
    return groups;
  }
  
  if (detections.length === 0) {
    return <div className="empty-state">No detections</div>;
  }
  
  if (!groupByShelf || grouped.ungrouped) {
    return (
      <div className="detection-list">
        <div className="group-toggle">
          <button onClick={() => setGroupByShelf(!groupByShelf)}>
            📦 Group by Shelf
          </button>
        </div>
        {(grouped.ungrouped || detections).map((det, index) => (
          <DetectionItem
            key={index}
            detection={det}
            index={det.originalIndex !== undefined ? det.originalIndex : index}
            isSelected={selectedIndex === (det.originalIndex !== undefined ? det.originalIndex : index)}
            onClick={onDetectionClick}
          />
        ))}
      </div>
    );
  }
  
  return (
    <div className="detection-list">
      <div className="group-toggle">
        <button onClick={() => setGroupByShelf(!groupByShelf)}>
          📋 Show All
        </button>
      </div>
      
      {Object.values(grouped).map((group, groupIdx) => (
        <div key={groupIdx} className="shelf-group">
          <div className="shelf-header">
            📦 Полка {group.shelfNum} ({group.products.length} продуктов)
          </div>
          <div className="shelf-products">
            {group.products.map((det) => (
              <DetectionItem
                key={det.originalIndex}
                detection={det}
                index={det.originalIndex}
                isSelected={selectedIndex === det.originalIndex}
                onClick={onDetectionClick}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetectionItem({ detection, index, isSelected, onClick }) {
  let confidenceClass = 'confidence-low';
  if (detection.confidence > 0.9) confidenceClass = 'confidence-high';
  else if (detection.confidence > 0.7) confidenceClass = 'confidence-medium';
  
  return (
    <div
      className={`detection-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onClick?.(index)}
    >
      <div className="detection-header">
        <span className="detection-index">#{index + 1}</span>
        <span className={`detection-confidence ${confidenceClass}`}>
          {(detection.confidence * 100).toFixed(1)}%
        </span>
      </div>
      
      <div className="detection-details">
        <div className="detail-row">
          <span className="detail-label">Class:</span>
          <span className="detail-value">{detection.class}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Label:</span>
          <span className="detail-value">{detection.label || `Product #${detection.class}`}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">BBox:</span>
          <span className="detail-value bbox">
            cx: {detection.cx?.toFixed(0) || '0'}, cy: {detection.cy?.toFixed(0) || '0'}<br/>
            w: {detection.width.toFixed(0)}, h: {detection.height.toFixed(0)}
          </span>
        </div>      </div>
    </div>
  );
}

export default DetectionList;

